
import React, { Fragment } from 'react';
import MainNavigation from '../MainNavigation';
import MainFooter from '../MainFooter';
import classes from './Pricing.module.css';
import { FaCheckCircle, FaRocket, FaCrown, FaShieldAlt, FaSyncAlt } from 'react-icons/fa';
import { useEffect } from 'react';

/**
 * ===== PRICING COMPONENT =====
 * This component displays different HMS (Hospital Management System) pricing plans
 * and allows users to select the plan that best fits their hospital's needs.
 * 
 * FEATURES:
 * - Shows 4 pricing tiers: Free Trial, Clinic, Polyclinic, and Hospital (IPD)
 * - Each plan includes a list of features with checkmarks
 * - One plan is highlighted as "BEST VALUE" (Clinic Version)
 * - Direct WhatsApp integration for quick inquiry
 * - Comprehensive feature comparison table
 * - Responsive grid layout for cards and table
 */
const Pricing = () => {
    // Scroll to top when component loads for better UX
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    /**
     * ===== PRICING PLANS DATA STRUCTURE =====
     * Each plan object contains:
     * - title: Plan name displayed to users
     * - price: Cost in rupees or "Contact Us" for custom pricing
     * - subtitle: Duration or description (one-time vs yearly)
     * - icon: React Icon component representing the plan
     * - maintenance: Target audience/use case
     * - features: Array of features/benefits included in this plan
     * - buttonText: Text displayed on the action button
     * - popular: Boolean flag to highlight the best-selling plan with a badge
     * - whatsappMsg: Pre-filled WhatsApp message when user clicks the button
     */
    const plans = [
        {
            title: "Free Trial",
            price: "FREE",
            subtitle: "15-Day Free Access",
            icon: <FaRocket />,
            maintenance: "Basic Support Only",
            features: [
                "Limited Patient Records",
                "Prescription Printing",
                "Email/Remote Support",
                "Help Tutorials access",
                "Demo Videos included",
                "Basic Clinic Review"
            ],
            buttonText: "Start Free Trial",
            popular: false,
            whatsappMsg: "Hello, I am interested in the Free Trial for Dr Tech Buddy HMS. Please help me get started."
        },
        {
            title: "Clinic Version",
            price: "₹17,700",
            subtitle: "One Time (or ₹8k/yr)",
            icon: <FaCrown />,
            maintenance: "Doctor with Reception",
            features: [
                "Unlimited Patient Records",
                "Regional Language Support",
                "Mobile Responsive UI",
                "Multipul Theme Colors",
                "Prescription & Header Designs",
                "WhatsApp Sharing Enabled",
                "Automated Reminders",
                "Email + Remote Assistance"
            ],
            buttonText: "Get Clinic Plan",
            popular: true, // This plan is marked as "BEST VALUE"
            whatsappMsg: "Hello, I am interested in the Clinic Version of Dr Tech Buddy (₹17,700 plan). Please provide more details."
        },
        {
            title: "Polyclinic Software",
            price: "₹24,000",
            subtitle: "One Time License",
            icon: <FaShieldAlt />,
            maintenance: "Multi-OPD Support",
            features: [
                "All Clinic Version Features",
                "Mobile Responsive Access",
                "Advanced Theme Customization",
                "Multiple OPD Desks",
                "Multi-Doctor Scheduling",
                "Shared Patients Database",
                "Receptionist Access Control",
                "Laboratory Integration"
            ],
            buttonText: "Get Polyclinic Plan",
            popular: false,
            whatsappMsg: "Hello, I am interested in the Polyclinic Software (₹24,000 plan). Please provide more details."
        },
        {
            title: "Beds Hospital",
            price: "Contact Us",
            subtitle: "IPD & Hospital Version",
            icon: <FaSyncAlt />,
            maintenance: "Full IPD Management",
            features: [
                "Comprehensive IPD Management",
                "Bed/Ward Management",
                "Hospital Billing & GST",
                "Detailed Discharge Summaries",
                "Admission/Consent Forms",
                "Consultant Payment Tracking",
                "Nursing Station Access",
                "IPD Desk Management"
            ],
            buttonText: "Request Pricing",
            popular: false,
            whatsappMsg: "Hello, I am interested in the Beds Hospital Software (IPD Version). Please provide pricing details."
        }
    ];

    // WhatsApp business number for inquiries
    const phoneNumber = "917559383489";

    /**
     * ===== WHATSAPP INQUIRY HANDLER =====
     * This function opens WhatsApp chat with pre-filled message
     * 
     * HOW IT WORKS:
     * 1. Takes a WhatsApp message as parameter
     * 2. Encodes the message as URL parameter
     * 3. Opens WhatsApp Web with the message pre-filled
     * 4. User just needs to click "Send" to inquire about the plan
     * 
     * BENEFITS:
     * - Direct contact without form filling
     * - Better conversion rate
     * - User comfort (familiar WhatsApp interface)
     */
    const handleAction = (msg) => {
        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    };

    return (
        <Fragment>
            <MainNavigation />
            <div className={classes.pricingContainer}>
                {/* ===== HEADER SECTION ===== */}
                <header className={classes.header}>
                    <h1 className={classes.title}>Simple, Transparent Pricing</h1>
                    <p className={classes.subtitle}>Pick the plan that works best for your hospital's growth.</p>
                </header>

                {/* ===== PRICING CARDS GRID ===== 
                    This section renders a responsive grid of pricing cards
                    
                    RENDERING PROCESS:
                    1. .map() iterates through the plans array
                    2. Each plan becomes a card
                    3. "popular" flag highlights the "BEST VALUE" plan with special styling
                    4. Features are rendered as a bulleted list with checkmark icons
                    5. Button opens WhatsApp with plan-specific message
                */}
                <div className={classes.pricingGrid}>
                    {plans.map((plan, index) => (
                        <div key={index} className={`${classes.priceCard} ${plan.popular ? classes.popularCard : ''}`}>
                            {/* "BEST VALUE" Badge - only shows for popular plan */}
                            {plan.popular && <div className={classes.badge}>BEST VALUE</div>}
                            
                            {/* Plan Icon */}
                            <div className={classes.planIcon}>
                                {plan.icon}
                            </div>
                            
                            {/* Plan Title and Price */}
                            <h3 className={classes.planTitle}>{plan.title}</h3>
                            <div className={classes.priceWrapper}>
                                <span className={classes.amount}>{plan.price}</span>
                                <span className={classes.duration}>{plan.subtitle}</span>
                            </div>

                            {/* Maintenance/Support Information */}
                            <div className={classes.maintenanceSection}>
                                <FaShieldAlt className={classes.shieldIcon} />
                                <span>{plan.maintenance}</span>
                            </div>

                            {/* Features List with Checkmarks */}
                            <ul className={classes.featureList}>
                                {plan.features.map((feature, idx) => (
                                    <li key={idx}>
                                        <FaCheckCircle className={classes.checkIcon} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            {/* Action Button - Different styling for popular plan */}
                            <button 
                                className={plan.popular ? classes.buttonPremium : classes.buttonOutline}
                                onClick={() => handleAction(plan.whatsappMsg)}
                            >
                                {plan.buttonText}
                            </button>
                        </div>
                    ))}
                </div>

                {/* ===== FEATURE COMPARISON TABLE SECTION ===== 
                    Detailed comparison of features across all plans
                    Helps users understand the differences between tiers
                */}
                <div className={classes.comparisonTitle}>
                    <h2>Feature Comparison</h2>
                    <p>Compare plans and select the one that fits your hospital needs.</p>
                </div>

                {/* Responsive Table with Feature Details */}
                <div className={classes.comparisonTableWrapper}>
                    <table className={classes.comparisonTable}>
                        {/* Table Headers - Plan names */}
                        <thead>
                            <tr>
                                <th>Features</th>
                                <th>Free Trial</th>
                                <th>Clinic Version</th>
                                <th>Polyclinic</th>
                                <th>Beds Hospital</th>
                            </tr>
                        </thead>
                        {/* Table Rows - Feature details for each plan */}
                        <tbody>
                            <tr><td>License Type</td><td>Free</td><td>Single Doc</td><td>Multi OPD</td><td>Multi IPD</td></tr>
                            <tr><td>Patients</td><td>Limited</td><td>Unlimited</td><td>Unlimited</td><td>Unlimited</td></tr>
                            <tr><td>Support</td><td>Email</td><td>Email+Remote</td><td>Email+Remote</td><td>Email+Remote</td></tr>
                            <tr><td>Training</td><td>Tutorials</td><td>Quick Guide</td><td>Quick Guide</td><td>Demo Videos</td></tr>
                            <tr><td>Software Updates</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
                            <tr><td>Prescription Printing</td><td>Basic</td><td>English + Regional</td><td>English + Regional</td><td>English + Regional</td></tr>
                            <tr><td>WhatsApp Integration</td><td>No</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
                            <tr><td>Cloud Backup</td><td>Local</td><td>Yes (1st Yr Free)</td><td>Yes (1st Yr Free)</td><td>Yes</td></tr>
                            <tr><td>Multi Doctor Appts</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td></tr>
                            <tr><td>Receptionist Desk</td><td>No</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
                            <tr><td>IPD Management</td><td>No</td><td>No</td><td>No</td><td>Full IPD</td></tr>
                            <tr><td>Mobile Responsive</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
                            <tr><td>Theme Colors</td><td>Basic</td><td>Multiple</td><td>Multiple</td><td>Multiple</td></tr>
                            <tr><td>Prescription Designs</td><td>Template 1</td><td>Multiple</td><td>Multiple</td><td>Multiple</td></tr>
                            <tr><td>Header Designs</td><td>Standard</td><td>Multiple Templates</td><td>Multiple Templates</td><td>Multiple Templates</td></tr>
                            <tr><td>Hospital Billing</td><td>No</td><td>No</td><td>No</td><td>Yes (Detailed)</td></tr>
                            <tr><td>Discharge Summary</td><td>No</td><td>No</td><td>No</td><td>Super Fast</td></tr>
                        </tbody>
                    </table>
                </div>

                {/* ===== FOOTNOTES & CLARIFICATIONS ===== 
                    Important notes and explanations about features
                */}
                <div className={classes.footnotes}>
                    <ol>
                        <li>Print prescriptions in English, Hindi, Marathi, and other regional languages.</li>
                        <li>Checkup and SMS reminders based on active credits.</li>
                        <li>App access is free for the first year for paid customers.</li>
                        <li>Cloud backup and software updates subject to AMC.</li>
                        <li>OPD Accounting includes Income and Expense tracking.</li>
                        <li>IPD Module includes Bed/Ward management and Discharge Summaries.</li>
                    </ol>
                </div>

                {/* Trust Badges Section */}
                <div className={classes.trustSection}>
                    <div className={classes.trustItem}>
                        <FaSyncAlt />
                        <span>7-Day Implementation Support</span>
                    </div>
                    <div className={classes.trustItem}>
                        <FaShieldAlt />
                        <span>Secure & Encrypted Data Handling</span>
                    </div>
                </div>
            </div>
            <MainFooter />
        </Fragment>
    );
};

export default Pricing;
